import React, { useContext, useState } from 'react';
import { FormControl, FormLabel, Input, Button, InputGroup, InputRightElement, IconButton, Flex, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff, MdCheckCircle, MdError } from 'react-icons/md';
import fetchData from './Util';
import { ConfigContext } from './Config';
import { useTranslation } from 'react-i18next';

const ChangePassword = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const config = useContext(ConfigContext);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    if (e.target.name === 'confirmPassword') {
      setIsConfirmPasswordTouched(true);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError(t('changePassword.passwordMismatch'));
      return;
    }

    const resetPasswordData = {
      pass: formData.password,
    };
    fetchData(`${config.backendUrl}/api/me?access_token=${config.accessToken}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resetPasswordData),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.title || t('changePassword.networkError'));
        }
        setSuccessMessage(t('changePassword.successMessage'));
        setError(null);
      })
      .catch((error) => {
        setError(error.message);
        setSuccessMessage(null);
      });
  };

  return (
    <form onSubmit={handleSubmit}>
      {successMessage && (
        <Alert status="success" marginBottom="20px">
          <AlertIcon />
          <AlertTitle>{t('changePassword.successTitle')}</AlertTitle>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert status="error" marginBottom="20px">
          <AlertIcon />
          <AlertTitle>{t('changePassword.errorTitle')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <FormControl marginBottom="20px">
        <FormLabel>{t('changePassword.newPasswordLabel')}</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={t('changePassword.newPasswordPlaceholder')}
          />
          <InputRightElement width="3rem">
            <IconButton
              aria-label={showPassword ? t('changePassword.hidePassword') : t('changePassword.showPassword')}
              variant="ghost"
              onClick={togglePasswordVisibility}
              icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            />
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <FormControl marginBottom="20px">
        <FormLabel>{t('changePassword.confirmPasswordLabel')}</FormLabel>
        <InputGroup>
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder={t('changePassword.confirmPasswordPlaceholder')}
            onBlur={() => setIsConfirmPasswordTouched(true)}
          />
          <InputRightElement width="3rem">
            <IconButton
              aria-label={showConfirmPassword ? t('changePassword.hidePassword') : t('changePassword.showPassword')}
              variant="ghost"
              onClick={toggleConfirmPasswordVisibility}
              icon={showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
            />
          </InputRightElement>
        </InputGroup>
        <FormControl marginBottom="5px">
          {isConfirmPasswordTouched && formData.password !== formData.confirmPassword && (
            <Flex alignItems="center" ml="5px">
              <MdError style={{ color: 'red' }} />
              <span style={{ marginLeft: '5px', color: 'red' }}>{t('changePassword.passwordMismatch')}</span>
            </Flex>
          )}
          {isConfirmPasswordTouched && formData.password === formData.confirmPassword && formData.confirmPassword !== '' && (
            <Flex alignItems="center" ml="5px">
              <MdCheckCircle style={{ color: 'green' }} />
              <span style={{ marginLeft: '5px', color: 'green' }}>{t('changePassword.passwordsMatch')}</span>
            </Flex>
          )}
        </FormControl>
      </FormControl>
      <Button type="submit" colorScheme="blue">
        {t('changePassword.submitButton')}
      </Button>
    </form>
  );
};

export default ChangePassword;
